import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LuChevronsDownUp, LuChevronsUpDown } from 'react-icons/lu';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { Modal, Tooltip, Tree } from 'antd';
import type { TreeDataNode } from 'antd';
import SearchInput from 'components/common/SearchInput/SearchInput';
import {
  SwitcherIcon,
  TreeErrorState,
  TreeLoadingState,
} from 'components/common/TreeStates/TreeStates';
import { UNSAVED_CHANGES_CANT_SAVE_MESSAGE } from 'constants/messageConstants';
import {
  CROP_KEY_PREFIX,
  FARM_KEY_PREFIX,
  FARMS_PAGE_PATH,
  FARMS_URL_PARAM_NODE,
  INPUT_KEY_PREFIX,
  ORG_KEY_PREFIX,
  PADDOCK_KEY_PREFIX,
} from 'constants/routeConstants';
import {
  DELETE_PLAN_API_ERROR_MESSAGE,
  DELETE_PLAN_BODY_SUFFIX,
  DELETE_PLAN_CANCEL_LABEL,
  DELETE_PLAN_LABEL,
  DELETE_PLAN_MODAL_TITLE,
  FARM_TREE_FARM_ERROR_MESSAGE,
  FARM_TREE_LOADING_MESSAGE,
  FARM_TREE_ORGANISATIONS_ERROR_MESSAGE,
  TREE_COLLAPSE_ALL_LABEL,
  TREE_EXPAND_ALL_LABEL,
  TREE_GROUP_BY_CROP,
  TREE_SEARCH_PLACEHOLDER,
  UNSAVED_CHANGES_DISCARD_LABEL,
  UNSAVED_CHANGES_KEEP_EDITING_LABEL,
  UNSAVED_CHANGES_LEAVE_INPUT_MESSAGE,
  UNSAVED_CHANGES_MODAL_TITLE,
  UNSAVED_CHANGES_MODAL_TRANSITION,
} from 'constants/uiConstants';
import { useLeavePlanAfterSaving } from 'context/PlanLeaveGuardContext';
import { useDeleteInput } from 'hooks/inputs/useDeleteInput';
import useFetchOrganisations from 'hooks/useFetchOrganisations';
import useFetchUserProfile from 'hooks/useFetchUserProfile';
import { useVisibleOrgData } from 'hooks/useVisibleOrgData';
import { IInput } from 'interfaces/IInput';
import { IPaddock } from 'interfaces/IPaddock';
import CreatePlanModal from 'pages/FarmsPage/CreatePlanModal';
import BrowserStorageUtils from 'utils/BrowserStorageUtils';
import {
  filterInputsByYear,
  findAddedCropKeys,
  getInputYear,
  groupInputsByCrop,
  groupInputsByFarmHierarchy,
  normaliseTreeSearch,
} from 'utils/FarmUtils';
import { toast } from 'utils/toast';
import { getApiErrorMessage } from 'utils/UtilityMethods';

import {
  getActiveOrganisationId,
  getIsSwitchingOrganisation,
} from '../../../redux/selectors/ActiveOrganisationSelectors';
import {
  getActiveTreeNodeKey,
  getSelectedFarmId,
  getSelectedPaddockId,
  getTreeGroupBy,
  getTreeYearFilter,
} from '../../../redux/selectors/FarmSelectors';
import { selectActiveGOrgId } from '../../../redux/selectors/GlobalCatalogueSelectors';
import { getSelectedInputId } from '../../../redux/selectors/InputSelectors';

import {
  buildFarmTreeData,
  createOrgNode,
  getInputDisplayName,
} from './FarmTreeViewNodes';

const areKeyArraysEqual = (a: React.Key[], b: React.Key[]): boolean =>
  a.length === b.length && a.every((key, index) => key === b[index]);

// Every node that can be expanded, i.e. has children — the key set behind
// "Expand all" and the auto-expand while a search is active.
const collectExpandableKeys = (nodes: TreeDataNode[]): React.Key[] =>
  nodes.flatMap((node) =>
    node.children && node.children.length > 0
      ? [node.key, ...collectExpandableKeys(node.children)]
      : []
  );

const TREE_CONTROL_BUTTON_CLASS =
  'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded text-gray-dark transition-colors hover:bg-gray-100 hover:text-charcoal';

interface FarmTreeViewProps {
  /** When true the tree skips its own isLoading gate and renders immediately
   *  with whatever data is already in cache. Used when the loading animation
   *  has been force-completed (e.g. offline mid-load). */
  forceReady?: boolean;
}

const FarmTreeView: React.FC<FarmTreeViewProps> = ({ forceReady = false }) => {
  const [, setSearchParams] = useSearchParams();
  const activeOrganisationId = useSelector(getActiveOrganisationId);
  const isSwitchingOrganisation = useSelector(getIsSwitchingOrganisation);
  const selectedFarmId = useSelector(getSelectedFarmId);
  const selectedPaddockId = useSelector(getSelectedPaddockId);
  const treeYearFilter = useSelector(getTreeYearFilter);
  const treeGroupBy = useSelector(getTreeGroupBy);
  const selectedInputId = useSelector(getSelectedInputId);

  const { isLoading: isUserLoading } = useFetchUserProfile();
  const {
    data: organisations = [],
    isLoading: isLoadingOrganisations,
    isError: isErrorOrganisations,
  } = useFetchOrganisations();

  const activeOrganisation = useMemo(
    () => organisations.find((org) => org.id === activeOrganisationId),
    [organisations, activeOrganisationId]
  );

  // Excluded farms and paddocks (and the plans linked to them) are hidden —
  // the same filtered data every other view reads (OP-2384, OP-2390).
  const {
    farms: treeFarms,
    inputs: treeInputs,
    farmsQuery: {
      isLoading: isLoadingFarms,
      isPending: isFarmsPending,
      isError: isErrorFarms,
    },
    inputsQuery: { isLoading: isLoadingInputs, isPending: isInputsPending },
  } = useVisibleOrgData(activeOrganisationId);

  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

  // Local UI state: not persisted, and cleared on org switch. Deferred so
  // typing stays responsive while the tree rebuilds behind it.
  const [searchQuery, setSearchQuery] = useState('');
  const searchTerm = normaliseTreeSearch(useDeferredValue(searchQuery));
  const activeNodeKey = useSelector(getActiveTreeNodeKey);

  // Writes the `node` param; useFarmsNavSync resolves/dispatches the Redux selection.
  // Pass { replace: true } for automatic corrections (e.g. year-filter clear)
  // so they don't pollute browser history.
  //
  // Guard: skip if the browser has already navigated away from the farms page
  // (e.g. a stale AntD Tree onSelect firing after navigate('/organisations')).
  // window.location.pathname is synchronously correct even before React re-renders.
  const navigateToNode = useCallback(
    (key: string | null, options?: { replace?: boolean }) => {
      if (!window.location.pathname.startsWith(FARMS_PAGE_PATH)) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (key) {
            next.set(FARMS_URL_PARAM_NODE, key);
          } else {
            next.delete(FARMS_URL_PARAM_NODE);
          }
          return next;
        },
        options?.replace ? { replace: true } : undefined
      );
    },
    [setSearchParams]
  );
  const [pendingSelectionKey, setPendingSelectionKey] = useState<string | null>(
    null
  );
  const [showSwitchDialog, setShowSwitchDialog] = useState(false);
  // What stops the open plan saving, when the switch dialog is shown for it.
  const [switchDialogReason, setSwitchDialogReason] = useState<string | null>(
    null
  );
  const leavePlanAfterSaving = useLeavePlanAfterSaving();
  const [paddockForNewPlan, setPaddockForNewPlan] = useState<IPaddock | null>(
    null
  );
  // Held while the unsaved-changes dialog decides whether the new plan proceeds.
  const [pendingPaddockForPlan, setPendingPaddockForPlan] =
    useState<IPaddock | null>(null);
  const [inputPendingDelete, setInputPendingDelete] = useState<IInput | null>(
    null
  );

  const gOrgId = useSelector(selectActiveGOrgId);

  // isLoadingInputs is intentionally included: the tree waits for the initial
  // inputs fetch so plan nodes appear together with farm/paddock nodes.
  // isErrorInputs is intentionally excluded: an input fetch failure is
  // non-fatal — Farm and Paddock nodes can still render without plan data.
  // FarmsPage shows a toast for the failure; the tree stays functional.
  // forceReady overrides all loading gates when the parent has force-completed
  // the progress animation (e.g. offline mid-load with partial data).
  const isLoading =
    !forceReady &&
    (isUserLoading ||
      isLoadingOrganisations ||
      isLoadingFarms ||
      isLoadingInputs ||
      isSwitchingOrganisation);
  const isError = isErrorOrganisations || isErrorFarms;

  const orgKey = activeOrganisationId
    ? `${ORG_KEY_PREFIX}${activeOrganisationId}`
    : '';

  const { paddockInputs, farmInputs, otherInputs } = useMemo(
    () => groupInputsByFarmHierarchy(treeFarms, treeInputs),
    [treeFarms, treeInputs]
  );

  // Creating a plan switches the selected input, which tears down the open
  // document's debounced save. Apply the same unsaved-changes guard used when
  // selecting another node, so a pending edit can't be stranded silently.
  const handleAddPlan = useCallback(
    (paddock: IPaddock) => {
      if (
        selectedInputId &&
        BrowserStorageUtils.hasPendingInputChanges(selectedInputId)
      ) {
        // Saves the open plan first; asks only if it can't be saved.
        const isAsking = leavePlanAfterSaving(
          () => setPaddockForNewPlan(paddock),
          (reason) => {
            setSwitchDialogReason(reason);
            setPendingPaddockForPlan(paddock);
            setShowSwitchDialog(true);
          }
        );
        if (!isAsking) {
          setSwitchDialogReason(null);
          setPendingPaddockForPlan(paddock);
          setShowSwitchDialog(true);
        }
        return;
      }

      setPaddockForNewPlan(paddock);
    },
    [leavePlanAfterSaving, selectedInputId]
  );

  const handleDeleteInput = useCallback((input: IInput) => {
    setInputPendingDelete(input);
  }, []);

  const { mutate: deleteInput, isPending: isDeletingInput } = useDeleteInput({
    activeOrganisationId,
  });

  const treeData = useMemo<TreeDataNode[]>(() => {
    const farmNodes = buildFarmTreeData(
      treeFarms,
      paddockInputs,
      farmInputs,
      otherInputs,
      treeYearFilter,
      treeGroupBy,
      treeInputs,
      selectedInputId,
      handleAddPlan,
      handleDeleteInput,
      searchTerm,
      // Each tick reads its own row, so a copy doesn't rebuild the tree.
      true
    );
    // The org row renders even with no child nodes. It is the only way to
    // switch organisation from the sidebar, so dropping it would strand a user
    // in an org that has no farms — or, in crop mode, in any year that happens
    // to have no plans.
    if (!activeOrganisation) return farmNodes;
    return [
      createOrgNode(
        activeOrganisation.orgName,
        activeOrganisation.id,
        farmNodes
      ),
    ];
  }, [
    treeFarms,
    paddockInputs,
    farmInputs,
    otherInputs,
    activeOrganisation,
    treeYearFilter,
    treeGroupBy,
    treeInputs,
    selectedInputId,
    handleAddPlan,
    handleDeleteInput,
    searchTerm,
  ]);

  const handleExpandAll = useCallback(() => {
    setExpandedKeys(collectExpandableKeys(treeData));
  }, [treeData]);

  // The org row has no switcher of its own, so it stays expanded — collapsing
  // it would leave no way to reopen the tree.
  const handleCollapseAll = useCallback(() => {
    setExpandedKeys(orgKey ? [orgKey] : []);
  }, [orgKey]);

  const selectedKeys = useMemo<React.Key[]>(
    () => (activeNodeKey ? [activeNodeKey] : []),
    [activeNodeKey]
  );

  const handleConfirmDelete = useCallback(() => {
    if (!inputPendingDelete || isDeletingInput) return;

    const { id } = inputPendingDelete;
    const inputKey = `${INPUT_KEY_PREFIX}${id}`;
    const wasSelected = selectedInputId === id;

    // Keep the local draft so a failed delete can hand it back — the plan
    // still exists in that case, and the edits would otherwise be gone.
    const editedInput = BrowserStorageUtils.getEditedInput(id);
    const originalInput = BrowserStorageUtils.getOriginalInput(id);

    // Drop the local draft and deselect BEFORE the request. Deselecting tears
    // down useInputDocumentDraft's debounced save for this document, so a
    // pending autosave cannot land after the delete and resurrect the plan.
    BrowserStorageUtils.removeEditedInput(id);
    BrowserStorageUtils.removeOriginalInput(id);

    // Clears node so the breadcrumb doesn't keep naming the deleted plan's farm/paddock.
    if (wasSelected) navigateToNode(null);

    deleteInput(id, {
      onSuccess: () => setInputPendingDelete(null),
      onError: (error) => {
        setInputPendingDelete(null);

        // Nothing was deleted, so put the draft and the selection back.
        if (editedInput) BrowserStorageUtils.storeEditedInput(editedInput);
        if (originalInput) {
          BrowserStorageUtils.storeOriginalInput(originalInput);
        }
        if (wasSelected) navigateToNode(inputKey);

        toast.error(getApiErrorMessage(error, DELETE_PLAN_API_ERROR_MESSAGE));
      },
    });
  }, [
    deleteInput,
    inputPendingDelete,
    isDeletingInput,
    navigateToNode,
    selectedInputId,
  ]);

  const handleSelect = useCallback(
    (keys: React.Key[]) => {
      const key = keys[0]?.toString();
      if (!key) return;

      if (key.startsWith(INPUT_KEY_PREFIX)) {
        const targetInputId = key.replace(INPUT_KEY_PREFIX, '');
        if (
          selectedInputId &&
          selectedInputId !== targetInputId &&
          BrowserStorageUtils.hasPendingInputChanges(selectedInputId)
        ) {
          // Saves the open plan, then opens the other one; asks whether to
          // discard only when it can't be saved, saying why.
          const isAsking = leavePlanAfterSaving(
            () => navigateToNode(key),
            (reason) => {
              setSwitchDialogReason(reason);
              setPendingSelectionKey(key);
              setShowSwitchDialog(true);
            }
          );
          if (!isAsking) {
            setSwitchDialogReason(null);
            setPendingSelectionKey(key);
            setShowSwitchDialog(true);
          }
          return;
        }
      }

      navigateToNode(key);
    },
    [leavePlanAfterSaving, navigateToNode, selectedInputId]
  );

  // Selects the freshly created plan. The created document is used directly
  // rather than looked up in `inputs`, whose closure may predate the cache write.
  const handlePlanCreated = useCallback(
    (createdInput: IInput) => {
      const paddockId = paddockForNewPlan?.paddockId ?? null;

      if (paddockId) {
        const paddockKey = `${PADDOCK_KEY_PREFIX}${paddockId}`;
        setExpandedKeys((prev) =>
          prev.includes(paddockKey) ? prev : [...prev, paddockKey]
        );
      }

      navigateToNode(`${INPUT_KEY_PREFIX}${createdInput.id}`);
      setPaddockForNewPlan(null);
    },
    [navigateToNode, paddockForNewPlan]
  );

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const key = activeNodeKey?.toString() ?? '';
      if (
        key.startsWith(FARM_KEY_PREFIX) ||
        key.startsWith(PADDOCK_KEY_PREFIX) ||
        key.startsWith(CROP_KEY_PREFIX)
      ) {
        setExpandedKeys((prev) =>
          prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
      }
    },
    [activeNodeKey]
  );

  // Snapshot of the user's own expansion, taken when a search starts and
  // restored when it is cleared, so searching doesn't lose their layout.
  const preSearchExpandedKeysRef = useRef<React.Key[] | null>(null);
  const appliedSearchTermRef = useRef('');

  // Selection reset now lives in useFarmsNavSync — this just collapses the
  // tree and drops any search, which belongs to the previous org's data.
  useEffect(() => {
    setExpandedKeys([]);
    setSearchQuery('');
    preSearchExpandedKeysRef.current = null;
  }, [activeOrganisationId]);

  // Expands every match while a search is active. Runs once per search term
  // (guarded by the ref) rather than on every tree rebuild, so the user can
  // still collapse branches of the results by hand.
  useEffect(() => {
    if (appliedSearchTermRef.current === searchTerm) return;
    const wasSearching = appliedSearchTermRef.current !== '';
    appliedSearchTermRef.current = searchTerm;

    if (!searchTerm) {
      const restoreKeys = preSearchExpandedKeysRef.current;
      preSearchExpandedKeysRef.current = null;
      if (restoreKeys) setExpandedKeys(restoreKeys);
      return;
    }

    if (!wasSearching) preSearchExpandedKeysRef.current = expandedKeys;
    setExpandedKeys(collectExpandableKeys(treeData));
  }, [searchTerm, treeData, expandedKeys]);

  // Seeds the expanded set once per organisation and grouping mode, not on
  // every data change. React Query hands back a new `inputs` array on each
  // refetch, so re-seeding on identity would silently re-expand anything the
  // user had collapsed. The two modes use different key namespaces, so a mode
  // switch replaces the set outright rather than merging into it.
  const seededExpansionRef = useRef<string | null>(null);

  // When the year filter changes to a specific year, check whether the currently
  // selected INPUT node is now hidden. If so, reset to the org root so the tree
  // selection and workspace content stay consistent.
  // Undated plans (getInputYear === null) are always visible — never reset those.
  useEffect(() => {
    if (!activeNodeKey?.startsWith(INPUT_KEY_PREFIX)) return;

    const inputId = activeNodeKey.replace(INPUT_KEY_PREFIX, '');
    const input = treeInputs.find((i) => i.id === inputId);
    if (!input) return;

    const inputYear = getInputYear(input);
    if (inputYear === null) return; // Undated — visible in every year

    if (typeof treeYearFilter === 'number' && inputYear !== treeYearFilter) {
      // Use replace so this automatic correction collapses with the year-change
      // push in history — pressing back then restores the pre-year-change URL.
      navigateToNode(null, { replace: true });
    }
  }, [activeNodeKey, treeInputs, treeYearFilter, navigateToNode]);

  useEffect(() => {
    if (!orgKey) return;

    // Both queries must have data before seeding. Use isPending (status ===
    // 'pending', i.e. no data yet) rather than isLoading (isPending &&
    // isFetching). When a query is disabled — e.g. while userData or
    // organisationsData is still loading — isLoading is false but isPending is
    // true. Seeding against an empty array at that point would latch the ref
    // and prevent any re-seed once the real data arrives.
    //
    // forceReady exception: when the parent has force-completed loading (e.g.
    // offline mid-load), skip the inputs guard so farms still expand. Inputs
    // will be absent but farms are the primary hierarchy.
    if (isFarmsPending) return;
    if (!forceReady && isInputsPending) return;

    const isCropMode = treeGroupBy === TREE_GROUP_BY_CROP;

    // Crop mode's entire node set is derived from the year, so changing year
    // there is a legitimate re-seed. Farm mode's structure is year-independent,
    // so re-seeding on year change would undo the user's manual collapses.
    const signature = isCropMode
      ? `${orgKey}|${treeGroupBy}|${treeYearFilter}`
      : `${orgKey}|${treeGroupBy}`;
    if (seededExpansionRef.current === signature) return;
    seededExpansionRef.current = signature;

    const nextExpandedKeys = isCropMode
      ? [
          orgKey,
          ...groupInputsByCrop(
            filterInputsByYear(treeInputs, treeYearFilter)
          ).map(({ cropKey }) => `${CROP_KEY_PREFIX}${cropKey}`),
        ]
      : [
          orgKey,
          ...treeFarms.map((farm) => `${FARM_KEY_PREFIX}${farm.id}`),
          ...treeFarms.flatMap((farm) =>
            farm.paddocks
              .filter((p) => (paddockInputs.get(p.paddockId) ?? []).length > 0)
              .map((p) => `${PADDOCK_KEY_PREFIX}${p.paddockId}`)
          ),
        ];

    setExpandedKeys((prev) =>
      areKeyArraysEqual(prev, nextExpandedKeys) ? prev : nextExpandedKeys
    );
  }, [
    treeFarms,
    paddockInputs,
    orgKey,
    treeGroupBy,
    treeInputs,
    treeYearFilter,
    isFarmsPending,
    isInputsPending,
    forceReady,
  ]);

  // Crop mode opens every crop when it's seeded, but a crop node that appears
  // later — a plan's crop changed to one with no other plans this year —
  // would start collapsed, so the plan seemed to vanish rather than move to
  // its new crop (OP-2560). Each new crop opens like the seeded ones did.
  const cropNodeKeys = useMemo(
    () =>
      treeGroupBy === TREE_GROUP_BY_CROP
        ? groupInputsByCrop(filterInputsByYear(treeInputs, treeYearFilter)).map(
            ({ cropKey }) => `${CROP_KEY_PREFIX}${cropKey}`
          )
        : [],
    [treeGroupBy, treeInputs, treeYearFilter]
  );
  const knownCropNodeKeysRef = useRef<{
    signature: string | null;
    keys: string[];
  }>({ signature: null, keys: [] });

  useEffect(() => {
    const known = knownCropNodeKeysRef.current;
    const signature = seededExpansionRef.current;
    knownCropNodeKeysRef.current = { signature, keys: cropNodeKeys };
    // A fresh seed (org, mode or year) expands its own set.
    if (!signature || known.signature !== signature) return;
    const added = findAddedCropKeys(known.keys, cropNodeKeys);
    if (added.length === 0) return;
    setExpandedKeys((prev) => [
      ...prev,
      ...added.filter((key) => !prev.includes(key)),
    ]);
  }, [cropNodeKeys]);

  useEffect(() => {
    if (selectedFarmId || selectedPaddockId || selectedInputId) {
      setExpandedKeys((prev) => {
        if (!selectedFarmId) return prev;
        const farmKey = `${FARM_KEY_PREFIX}${selectedFarmId}`;
        if (prev.includes(farmKey)) return prev;
        return [...prev, farmKey];
      });
    }
  }, [selectedFarmId, selectedPaddockId, selectedInputId]);

  // Scroll the selected node into view after expand animations settle.
  // antd's Tree.scrollTo only works in virtual mode (height prop required),
  // so we use native scrollIntoView on the selected DOM node instead.
  // .custom-tree is unique to this sidebar tree — safe to scope the query.
  useEffect(() => {
    if (!activeNodeKey) return;
    const id = setTimeout(() => {
      document
        .querySelector('.custom-tree [aria-selected="true"]')
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 200);
    return () => clearTimeout(id);
  }, [activeNodeKey]);

  if (isLoading) {
    return <TreeLoadingState message={FARM_TREE_LOADING_MESSAGE} />;
  }

  if (isError) {
    return (
      <TreeErrorState
        message={
          isErrorOrganisations
            ? FARM_TREE_ORGANISATIONS_ERROR_MESSAGE
            : FARM_TREE_FARM_ERROR_MESSAGE
        }
      />
    );
  }

  return (
    <>
      <div className="mb-1 flex items-center gap-1 px-1">
        <div className="min-w-0 flex-1">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={TREE_SEARCH_PLACEHOLDER}
            autoFocus={false}
          />
        </div>
        <Tooltip title={TREE_EXPAND_ALL_LABEL}>
          <button
            type="button"
            aria-label={TREE_EXPAND_ALL_LABEL}
            className={TREE_CONTROL_BUTTON_CLASS}
            onClick={handleExpandAll}
          >
            <LuChevronsUpDown size={18} />
          </button>
        </Tooltip>
        <Tooltip title={TREE_COLLAPSE_ALL_LABEL}>
          <button
            type="button"
            aria-label={TREE_COLLAPSE_ALL_LABEL}
            className={TREE_CONTROL_BUTTON_CLASS}
            onClick={handleCollapseAll}
          >
            <LuChevronsDownUp size={18} />
          </button>
        </Tooltip>
      </div>

      <Tree
        className="custom-tree mt-1 select-none overflow-y-auto overflow-x-hidden pr-2"
        treeData={treeData}
        expandedKeys={expandedKeys}
        selectedKeys={selectedKeys}
        onSelect={handleSelect}
        blockNode
        onDoubleClick={handleDoubleClick}
        selectable={true}
        expandAction={false}
        onExpand={(keys) => setExpandedKeys(keys)}
        switcherIcon={(props: { expanded?: boolean }) => (
          <SwitcherIcon expanded={props.expanded ?? false} />
        )}
      />

      <Modal
        open={showSwitchDialog}
        // Fades in where it sits, rather than zooming from the tree click.
        transitionName={UNSAVED_CHANGES_MODAL_TRANSITION}
        title={UNSAVED_CHANGES_MODAL_TITLE}
        okText={UNSAVED_CHANGES_DISCARD_LABEL}
        cancelText={UNSAVED_CHANGES_KEEP_EDITING_LABEL}
        onOk={() => {
          if (selectedInputId) {
            BrowserStorageUtils.removeEditedInput(selectedInputId);
            BrowserStorageUtils.removeOriginalInput(selectedInputId);
          }
          if (pendingSelectionKey) {
            navigateToNode(pendingSelectionKey);
          }
          if (pendingPaddockForPlan) {
            setPaddockForNewPlan(pendingPaddockForPlan);
          }
          setPendingSelectionKey(null);
          setPendingPaddockForPlan(null);
          setShowSwitchDialog(false);
        }}
        onCancel={() => {
          setPendingSelectionKey(null);
          setPendingPaddockForPlan(null);
          setShowSwitchDialog(false);
        }}
      >
        <p className="text-gray-dark">
          {switchDialogReason
            ? UNSAVED_CHANGES_CANT_SAVE_MESSAGE(switchDialogReason)
            : UNSAVED_CHANGES_LEAVE_INPUT_MESSAGE}
        </p>
      </Modal>

      {inputPendingDelete && (
        <Modal
          open
          title={DELETE_PLAN_MODAL_TITLE}
          okText={DELETE_PLAN_LABEL}
          cancelText={DELETE_PLAN_CANCEL_LABEL}
          okButtonProps={{
            className: 'custom-popconfirm-ok',
            loading: isDeletingInput,
          }}
          cancelButtonProps={{
            className: 'custom-popconfirm-cancel',
            disabled: isDeletingInput,
          }}
          mask={{ closable: !isDeletingInput }}
          keyboard={!isDeletingInput}
          closable={!isDeletingInput}
          onOk={handleConfirmDelete}
          onCancel={() => setInputPendingDelete(null)}
        >
          <p className="text-gray-dark">
            <span className="font-semibold">
              {getInputDisplayName(inputPendingDelete)}
            </span>{' '}
            {DELETE_PLAN_BODY_SUFFIX}
          </p>
        </Modal>
      )}

      {/* Mounted only while open, so the crop/unit catalogue queries it owns
          do not run for every user viewing the sidebar, and each opening gets
          a fresh form with no carried-over validation state. */}
      {paddockForNewPlan && (
        <CreatePlanModal
          open
          paddock={paddockForNewPlan}
          activeOrganisationId={activeOrganisationId}
          gOrgId={gOrgId}
          onClose={() => setPaddockForNewPlan(null)}
          onCreated={handlePlanCreated}
        />
      )}
    </>
  );
};

export default FarmTreeView;
